import React, { useState, useEffect, useMemo } from 'react';
import { useMessages } from '../../hooks/useMessages';
import { useMessageUrgency } from '../../hooks/useMessageUrgency';
import { useWebSocketConnection } from '../../contexts/WebSocketContext';
import { Message } from '../../types/messages';
import { MessageDetail } from './MessageDetail';
import { sendToTerminal } from '../../lib/terminalLogger';
import { X } from 'lucide-react';
import '../../assets/css/components/messages-modal.css';

const LOG_PREFIX = '[MESSAGES_MODAL]';

interface MessagesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type TabType = 'all' | 'unread' | 'action';

/**
 * Modal de mensagens - Layout split com lista e detalhe
 */
export function MessagesModal({ isOpen, onClose }: MessagesModalProps) {
  const { messages, unreadCount, loading, markAsRead, markMultipleAsRead, refresh } = useMessages();
  const { isConnected } = useWebSocketConnection();
  const { isResponseDeadlineExpired } = useMessageUrgency();

  const [activeTab, setActiveTab] = useState<TabType>('all');
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isMarkingVisible, setIsMarkingVisible] = useState(false);
  const itemsPerPage = 12;

  // Filtrar e ordenar mensagens
  const filteredMessages = useMemo(() => {
    let filtered: Message[];

    switch (activeTab) {
      case 'unread':
        filtered = messages.filter((m) => !m.lida);
        break;
      case 'action':
        filtered = messages.filter(
          (m) => m.requer_resposta && !m.resposta_enviada && !isResponseDeadlineExpired(m)
        );
        break;
      default:
        filtered = [...messages];
    }

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((m) =>
        m.titulo.toLowerCase().includes(term) ||
        m.mensagem.toLowerCase().includes(term)
      );
    }

    // Prioridade de ordenacao:
    // 1) nao lidas com acao pendente, 2) nao lidas, 3) lidas, 4) data
    return filtered.sort((a, b) => {
      const aActionPending = !a.lida && a.requer_resposta && !a.resposta_enviada;
      const bActionPending = !b.lida && b.requer_resposta && !b.resposta_enviada;
      if (aActionPending && !bActionPending) return -1;
      if (!aActionPending && bActionPending) return 1;

      if (!a.lida && b.lida) return -1;
      if (a.lida && !b.lida) return 1;

      return new Date(b.criada_em).getTime() - new Date(a.criada_em).getTime();
    });
  }, [messages, activeTab, searchTerm, isResponseDeadlineExpired]);

  const paginatedMessages = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredMessages.slice(start, start + itemsPerPage);
  }, [filteredMessages, currentPage]);

  const totalPages = Math.max(1, Math.ceil(filteredMessages.length / itemsPerPage));
  const actionCount = messages.filter(
    (m) => m.requer_resposta && !m.resposta_enviada && !isResponseDeadlineExpired(m)
  ).length;
  const visibleUnreadIds = paginatedMessages.filter((m) => !m.lida).map((m) => m.id);

  // Carregar ao abrir
  useEffect(() => {
    if (isOpen) {
      refresh({ includeExpired: false });
      sendToTerminal(`${LOG_PREFIX} aberto`, { unreadCount, total: messages.length });
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchTerm]);

  // Selecionar primeira mensagem nao lida ao abrir/mudar filtro
  useEffect(() => {
    if (paginatedMessages.length > 0 && !selectedMessage) {
      const firstUnread = paginatedMessages.find((m) => !m.lida);
      setSelectedMessage(firstUnread || paginatedMessages[0]);
    } else if (paginatedMessages.length === 0) {
      setSelectedMessage(null);
    }
  }, [paginatedMessages, selectedMessage]);

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'agora';
      if (diffMins < 60) return `${diffMins}min`;
      if (diffHours < 24) return `${diffHours}h`;
      if (diffDays < 7) return `${diffDays}d`;
      return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
    } catch {
      return dateString;
    }
  };

  const getMessageStatus = (msg: Message) => {
    const expired = isResponseDeadlineExpired(msg);
    const needsReply = msg.requer_resposta && !msg.resposta_enviada;

    if (expired) return 'expired';
    if (needsReply && !msg.lida) return 'action';
    if (!msg.lida) return 'new';
    return 'read';
  };

  const handleSelectMessage = (msg: Message) => {
    setSelectedMessage(msg);
  };

  const handleMessageUpdated = () => {
    refresh({ includeExpired: false });
  };

  const handleMarkVisibleAsRead = async () => {
    if (visibleUnreadIds.length === 0 || isMarkingVisible) return;
    try {
      setIsMarkingVisible(true);
      await markMultipleAsRead(visibleUnreadIds);
      await refresh({ includeExpired: false });
    } finally {
      setIsMarkingVisible(false);
    }
  };

  // Fechar ao clicar no backdrop
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100]"
      onClick={handleBackdropClick}
    >
      <div className="messages-modal bg-navy-800 shadow-xl rounded-lg animate-fadeIn">
        <div className="mm-container">
          {/* Sidebar - Lista de mensagens */}
          <aside className="mm-sidebar">
            <header className="mm-sidebar-header">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="mm-sidebar-title">Mensagens</h2>
                  <p className="mm-sidebar-subtitle">
                    {isConnected ? 'Conectado' : 'Offline'}
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="mm-close-btn"
                  title="Fechar"
                >
                  <X size={18} />
                </button>
              </div>
            </header>

            <nav className="mm-tabs">
              <button
                onClick={() => setActiveTab('all')}
                className={`mm-tab ${activeTab === 'all' ? 'active' : ''}`}
              >
                Todas
              </button>
              <button
                onClick={() => setActiveTab('unread')}
                className={`mm-tab ${activeTab === 'unread' ? 'active' : ''}`}
              >
                Nao lidas
                {unreadCount > 0 && <span className="mm-tab-badge">{unreadCount}</span>}
              </button>
              <button
                onClick={() => setActiveTab('action')}
                className={`mm-tab ${activeTab === 'action' ? 'active' : ''}`}
              >
                Acao
                {actionCount > 0 && <span className="mm-tab-badge">{actionCount}</span>}
              </button>
            </nav>

            <div className="px-3 py-2 border-b border-navy-700 flex flex-col gap-2">
              <input
                type="text"
                placeholder="Buscar mensagens..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-2 py-1.5 text-xs bg-navy-900 border border-navy-700 rounded text-white placeholder-gray-500 focus:outline-none focus:border-blue-400"
              />
              <button
                type="button"
                onClick={handleMarkVisibleAsRead}
                disabled={visibleUnreadIds.length === 0 || isMarkingVisible}
                className="w-full px-2 py-1.5 text-xs rounded bg-navy-700 text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-navy-600 transition-colors"
              >
                {isMarkingVisible ? 'Marcando...' : `Marcar visiveis como lidas (${visibleUnreadIds.length})`}
              </button>
            </div>

            <div className="mm-list">
              {loading ? (
                <div className="mm-loading">
                  <div className="mm-spinner" />
                </div>
              ) : paginatedMessages.length === 0 ? (
                <div className="mm-loading">
                  <span>Nenhuma mensagem</span>
                </div>
              ) : (
                paginatedMessages.map((msg) => {
                  const status = getMessageStatus(msg);
                  const isActive = selectedMessage?.id === msg.id;
                  const pendingAction = msg.requer_resposta && !msg.resposta_enviada;
                  const expired = isResponseDeadlineExpired(msg);

                  return (
                    <div
                      key={msg.id}
                      onClick={() => handleSelectMessage(msg)}
                      className={`mm-item ${isActive ? 'active' : ''} ${!msg.lida ? 'unread' : ''}`}
                    >
                      <span className={`mm-item-dot ${status}`} />
                      <div className="mm-item-content">
                        <div className="mm-item-title">{msg.titulo}</div>
                        <div className="mm-item-preview">{msg.mensagem}</div>
                        {pendingAction && (
                          <div className={`mt-1 text-[10px] ${expired ? 'text-red-400' : 'text-yellow-400'}`}>
                            {expired ? 'Prazo expirado' : 'Aguardando resposta'}
                          </div>
                        )}
                      </div>
                      <span className="mm-item-time">{formatDate(msg.criada_em)}</span>
                    </div>
                  );
                })
              )}
            </div>

            {totalPages > 1 && (
              <div className="px-3 py-2 border-t border-navy-700 flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-2 py-1 text-xs rounded bg-navy-700 disabled:opacity-50"
                >
                  Anterior
                </button>
                <span className="text-[11px] text-gray-400">Pagina {currentPage}/{totalPages}</span>
                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-2 py-1 text-xs rounded bg-navy-700 disabled:opacity-50"
                >
                  Proxima
                </button>
              </div>
            )}
          </aside>

          {/* Detail - Detalhe da mensagem */}
          <main className="mm-detail">
            {selectedMessage ? (
              <MessageDetail
                message={selectedMessage}
                onMarkAsRead={markAsRead}
                onMessageUpdated={handleMessageUpdated}
              />
            ) : (
              <div className="mm-detail-empty">
                <span className="mm-detail-empty-icon">Mensagens</span>
                <p>Selecione uma mensagem</p>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
